import React, { Component } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import Table from '@cloudscape-design/components/table';
import Pagination from '@cloudscape-design/components/pagination';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Preferences from '@cloudscape-design/components/collection-preferences';
import Alert from '@cloudscape-design/components/alert';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Modal from '@cloudscape-design/components/modal';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { configStore } from '../stores/config';
import {  playerBanStore } from "../stores/playerBan";
import { playerBanAddModalStore } from "../stores/playerBanAddModal";
import Grid from "@cloudscape-design/components/grid";
import Select from "@cloudscape-design/components/select";
import Input from "@cloudscape-design/components/input";
import { playerBanEditModalStore } from "../stores/playerBanEditModal";

class PlayerBan extends Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedItems: [],
			showDeleteModal: false,
			isDeleting: false,
		};
	}

	componentDidMount() {
		this.props.Interface.loadPlayerBan();
	}

	handleKeyPress = (event) => {
		if (event.detail.key === 'Enter') {
			setTimeout(() => {
				this.props.Interface.loadPlayerBan();
			}, 0);
		}
	};

	handleDeleteSelected = () => {
		if (this.state.selectedItems.length === 0) {
			this.props.Interface.addNoty('Error', 'No items selected for deletion');
			return;
		}
		this.setState({ showDeleteModal: true });
	};

	confirmDelete = async () => {
		const { selectedItems } = this.state;
		const { Interface } = this.props;

		this.setState({ isDeleting: true });

		try {
			const ids = selectedItems.map((item) => item.id);
			await Interface.request('set', {
				class: 'data',
				module: 'playerBan',
				method: 'delete',
			}, { id: ids });

			Interface.addNoty('Success', `${ids.length} player ban(s) deleted successfully`);
			this.setState({ selectedItems: [], showDeleteModal: false });
			Interface.loadPlayerBan();
		} catch (error) {
			Interface.addNoty('Error', error.text || error.toString());
		} finally {
			this.setState({ isDeleting: false });
		}
	};

	render() {
		const { playerBan, setPlayerBan, config, setConfig, setPlayerBanAddModal, playerBanAddModal, setPlayerBanEditModal, playerBanEditModal } = this.props;
		const { selectedItems, showDeleteModal, isDeleting } = this.state;

		return (
			<Container>
				{playerBan.loaded && playerBan.error.text.length ? (
					<Box margin={{ bottom: 'm' }}>
						<Alert
							type="error"
							header="Error loading data"
							dismissible
							onDismiss={() => setPlayerBan({ ...playerBan, error: { text: '', code: 0, timestamp: 0 } })}
						>
							<Box variant="p">{playerBan.error.text}</Box>
							<hr />
							<Box variant="p">
								Request error code: {playerBan.error.code} | Request timestamp: {playerBan.error.timestamp}
							</Box>
						</Alert>
					</Box>
				) : null}

				<Modal
					visible={showDeleteModal}
					onDismiss={() => this.setState({ showDeleteModal: false })}
					header="Confirm Deletion"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button
									variant="link"
									onClick={() => this.setState({ showDeleteModal: false })}
									disabled={isDeleting}
								>
									Close
								</Button>
								<Button
									variant="primary"
									onClick={this.confirmDelete}
									loading={isDeleting}
									disabled={isDeleting}
								>
									Apply
								</Button>
							</SpaceBetween>
						</Box>
					}
				>
					Are you sure you want to delete {selectedItems.length} selected player ban(s)? This action cannot be undone.
				</Modal>

				<Table
					stickyHeader
					trackBy="id"
					selectionType="multi"
					selectedItems={selectedItems}
					onSelectionChange={({ detail }) => this.setState({ selectedItems: detail.selectedItems })}
					loading={!playerBan.loaded}
					sortingColumn={{ sortingField: playerBan.sorting.sortingColumn.column }}
					sortingDescending={playerBan.sorting.isDescending}
					onSortingChange={(sorting) => {
						setPlayerBan({
							...playerBan,
							sorting: {
								...playerBan.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? 'ASC' : 'DESC',
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => {
							this.props.Interface.loadPlayerBan();
						}, 250);
					}}
					columnDefinitions={[
						{
							id: 'id',
							header: 'ID',
							sortingField: 'id',
							cell: (item) => (
								<Button
									variant="link"
									onClick={() => {
										const preItem = cloneDeep(item);
										const { id } = preItem;

										delete preItem.id;
										delete preItem.createdAt;

										setPlayerBanEditModal({
											...playerBanEditModal,
											id,
											...preItem,
											show: true,
											banType: preItem.till ? {  value: "date", label: "Date" } : { value: "forbidden", label: "Forbidden" },
											date: preItem.till ? preItem.till: false,
										});
									}}
								>
									{item.id}
								</Button>
							),
						},
						{
							id: "uid",
							header: "UID",
							sortingField: "uid",
							cell: (item) => (
								<Button
									iconName={
										typeof item.verify !== "undefined" && item.verify.status === 3
											? "status-stopped"
											: typeof item.verify !== "undefined" && item.verify.verified
												? "status-positive"
												: "status-in-progress"
									}
									className={this.props.styles?.["btnUID"]}
									variant="normal"
									onClick={() => {
										this.props.Interface.loadInfo(item.uid, item.brand);
									}}
									children={item.uid}
								/>
							),
						},
						{ id: 'reason', header: 'Reason', sortingField: 'reason', cell: (item) => item.reason || '-' },
						{
							id: 'till',
							header: 'Till',
							sortingField: 'till',
							cell: (item) => item.till? new Date(item.till).toLocaleString(): "Forbidden",
						},
						{
							id: 'createdAt',
							header: 'Created At',
							sortingField: 'createdAt',
							cell: (item) => new Date(item.createdAt).toLocaleString(),
						},
					]}
					items={playerBan.data}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: 'Player Bans per page', options: config.playerBan.preferences.perPage }}
							preferences={{ pageSize: config.playerBan.perPage }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem('playerBan.perPage', preferences.detail.pageSize);
								setConfig({ ...config, playerBan: { ...config.playerBan, perPage: preferences.detail.pageSize } });
								setTimeout(() => {
									this.props.Interface.loadPlayerBan();
								}, 250);
							}}
						/>
					}
					pagination={
						<Pagination
							disabled={!playerBan.loaded}
							currentPageIndex={playerBan.pagination.current}
							pagesCount={playerBan.pagination.total}
							onChange={(page) => {
								setPlayerBan({ ...playerBan, pagination: { ...playerBan.pagination, current: page.detail.currentPageIndex } });
								setTimeout(() => {
									this.props.Interface.loadPlayerBan();
								}, 250);
							}}
						/>
					}
					header={
						<Header
							variant="h1"
							description="Manage player bans"
							counter={`(${playerBan.pagination.count})`}
							actions={
								<SpaceBetween direction="horizontal" size="xs">
										<Button
											variant="normal"
											iconName="remove"
											onClick={this.handleDeleteSelected}
											disabled={isDeleting || !selectedItems.length}
										>
											Delete Selected ({selectedItems.length})
										</Button>
									<Button
										iconName="add-plus"
										disabled={!playerBan.loaded}
										variant="primary"
										onClick={() => {
											setPlayerBanAddModal({ ...playerBanAddModal, show: true });
										}}
									>
										Add Player Ban
									</Button>
								</SpaceBetween>
							}
						>
							Player Bans
						</Header>
					}
					filter={
						<Box
							margin={{ bottom: "m" }}
							children={
								<Grid
									gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}
									children={
										<>
											<Select
												filteringType="auto"
												disabled={!playerBan.loaded}
												placeholder="Type"
												selectedOption={playerBan.currentSearchType}
												onChange={(option) => {
													option.detail.selectedOption.type !== playerBan.currentSearchType &&
													setPlayerBan({
														...playerBan,
														currentSearchType: option.detail.selectedOption,
													});
												}}
												options={[{ value: "uid", label: "uid" }, { value: "reason", label: "reason" }, { value: "id", label: "id" }]}
											/>
											<Input
												disabled={!playerBan.loaded}
												onKeyDown={this.handleKeyPress}
												disableBrowserAutocorrect={true}
												placeholder="id, name"
												inputMode="search"
												type="search"
												value={playerBan.currentSearch}
												onChange={(event) => {
													setPlayerBan({
														...playerBan,
														currentSearch: event.detail.value,
													});
												}}
											/>
										</>
									}
								/>
							}
						/>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No player bans found</b>
							<Box padding={{ bottom: 's' }} variant="p" color="inherit">
								No player bans match the current criteria.
							</Box>
							<Button
								onClick={() => setPlayerBanAddModal({ ...playerBanAddModal, show: true })}
							>
								Add Player Ban
							</Button>
						</Box>
					}
				/>
			</Container>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const playerBan = useRecoilValue(playerBanStore);
		const setPlayerBan = useSetRecoilState(playerBanStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const playerBanAddModal = useRecoilValue(playerBanAddModalStore);
		const setPlayerBanAddModal = useSetRecoilState(playerBanAddModalStore);
		const playerBanEditModal = useRecoilValue(playerBanEditModalStore);
		const setPlayerBanEditModal = useSetRecoilState(playerBanEditModalStore);

		return (
			<Component
				{...props}
				playerBan={playerBan}
				setPlayerBan={setPlayerBan}
				config={config}
				setConfig={setConfig}
				playerBanAddModal={playerBanAddModal}
				setPlayerBanAddModal={setPlayerBanAddModal}
				playerBanEditModal={playerBanEditModal}
				setPlayerBanEditModal={setPlayerBanEditModal}
			/>
		);
	};
}

export default withRecoil(PlayerBan);